import { Link } from 'react-router-dom';
import {
  FaCloud, FaCode, FaChartBar, FaShieldAlt, FaServer, FaLightbulb,
  FaArrowRight, FaCheckCircle, FaDatabase, FaCogs,
} from 'react-icons/fa';
import { HiOutlineChip } from 'react-icons/hi';

const services = [
  {
    icon: <FaCloud className="text-2xl text-cyan-500" />,
    title: 'Cloud Solutions',
    desc: 'Seamless migration and management of cloud infrastructure on AWS, Azure, and GCP.',
    bg: 'bg-cyan-50',
  },
  {
    icon: <FaCode className="text-2xl text-blue-500" />,
    title: 'Software Development',
    desc: 'Custom enterprise applications built with modern frameworks and best practices.',
    bg: 'bg-blue-50',
  },
  {
    icon: <FaChartBar className="text-2xl text-violet-500" />,
    title: 'Data & Analytics',
    desc: 'Transform raw data into actionable insights with Power BI, SQL, and ML pipelines.',
    bg: 'bg-violet-50',
  },
  {
    icon: <FaShieldAlt className="text-2xl text-emerald-500" />,
    title: 'Cybersecurity',
    desc: 'Protect your digital assets with comprehensive security audits and compliance.',
    bg: 'bg-emerald-50',
  },
];

const stats = [
  { value: '10+', label: 'Clients Served' },
  { value: '15+', label: 'Years Experience' },
  { value: '20+', label: 'Projects Delivered' },
  { value: '95%', label: 'Client Retention' },
];

const techStack = [
  'C# / .NET', 'Java', 'React', 'Angular', 'Vue.js',
  'Azure', 'AWS', 'SQL Server', 'Oracle', 'Power BI',
  'Python', 'Node.js', 'Docker', 'Kubernetes', 'Terraform',
];

const whyUs = [
  { icon: <FaServer size={22} />, title: 'Managed Services', desc: '24/7 infrastructure monitoring and proactive support with guaranteed SLAs.' },
  { icon: <FaCloud size={22} />, title: 'Cloud-First', desc: 'Azure, AWS, and GCP certified engineers delivering scalable cloud architecture.' },
  { icon: <FaLightbulb size={22} />, title: 'Innovation', desc: 'Cutting-edge AI, ML, and automation capabilities for digital transformation.' },
  { icon: <FaShieldAlt size={22} />, title: 'Security', desc: 'SOC 2 compliant processes, regular audits, and enterprise-grade data protection.' },
];

// eslint-disable-next-line no-unused-vars
const testimonials = [
  {
    name: 'David Chen',
    role: 'CTO, FinTech Innovations',
    text: "MARVY transformed our legacy infrastructure into a modern cloud-native platform in just 6 months. Their team's expertise was invaluable.",
    initials: 'DC',
    color: 'bg-blue-600',
  },
  {
    name: 'Maria Rodriguez',
    role: 'VP Engineering, HealthCare Plus',
    text: 'The .NET development team they placed with us delivered beyond expectations. Highly professional and technically sharp.',
    initials: 'MR',
    color: 'bg-violet-600',
  },
  {
    name: 'James Park',
    role: 'Director IT, GovTech Solutions',
    text: 'Their data analytics practice helped us unlock insights we never knew existed. Power BI dashboards changed how our leadership makes decisions.',
    initials: 'JP',
    color: 'bg-emerald-600',
  },
];

export default function Home() {
  return (
    <div className="bg-white">

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#0c1b4d] via-[#1a3170] to-[#1d4ed8] text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '32px 32px' }}
        />
        <div className="absolute top-20 right-20 w-72 h-72 bg-cyan-500 opacity-10 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-blue-400 opacity-10 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 py-24 md:py-36">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-white bg-opacity-10 border border-cyan-400 border-opacity-40 rounded-full px-4 py-1.5 text-sm text-cyan-300 mb-7">
              <HiOutlineChip className="text-cyan-400" />
              IT Consulting &amp; Managed Services
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold leading-tight mb-6 tracking-tight">
              Transforming Business<br />
              <span className="text-cyan-400">Through Technology</span>
            </h1>
            <p className="text-lg text-blue-200 mb-8 leading-relaxed max-w-xl">
              MARVY IT Services delivers strategic technology consulting, skilled IT resources, and digital transformation solutions that drive measurable results for your organization.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/services" className="inline-flex items-center gap-2 px-6 py-3 bg-cyan-500 hover:bg-cyan-400 text-white font-semibold rounded-xl transition-colors shadow-lg">
                Explore Services <FaArrowRight size={13} />
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 border border-white border-opacity-40 text-white hover:bg-white hover:bg-opacity-10 font-semibold rounded-xl transition-colors">
                Get in Touch
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 56" className="w-full fill-white" preserveAspectRatio="none">
            <path d="M0,56 C360,0 1080,56 1440,0 L1440,56 Z" />
          </svg>
        </div>
      </section>

      {/* Stats */}
      <section className="py-14 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-4xl font-extrabold text-[#0c1b4d]">{s.value}</div>
                <div className="text-gray-500 text-sm mt-1.5 font-medium">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-cyan-600 text-sm font-semibold uppercase tracking-widest">What We Do</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0c1b4d] mt-2 mb-4">Our Core Services</h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              End-to-end IT solutions that help organizations scale, innovate, and stay competitive.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((s, i) => (
              <div key={i} className="bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-default">
                <div className={`w-12 h-12 ${s.bg} rounded-xl flex items-center justify-center mb-4`}>
                  {s.icon}
                </div>
                <h3 className="text-base font-semibold text-[#0c1b4d] mb-2">{s.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/services" className="inline-flex items-center gap-2 text-blue-600 font-semibold hover:text-blue-800 transition-colors">
              View All Services <FaArrowRight size={12} />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-[#0c1b4d] text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <span className="text-cyan-400 text-sm font-semibold uppercase tracking-widest">Why MARVY</span>
              <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">Your Trusted Technology Partner</h2>
              <p className="text-blue-200 mb-8 leading-relaxed">
                We bring together deep domain expertise, proven delivery methodologies, and top-tier talent to solve your most complex technology challenges — on time and within budget.
              </p>
              <ul className="space-y-3.5">
                {[
                  'Access to 200+ pre-vetted IT consultants nationwide',
                  'Industry expertise across Finance, Healthcare & Government',
                  'Agile delivery model with transparent KPI tracking',
                  'End-to-end project lifecycle management',
                  'US-based team with nationwide client placement',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <FaCheckCircle className="text-cyan-400 mt-0.5 flex-shrink-0" size={15} />
                    <span className="text-blue-100 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <Link to="/about" className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-white font-semibold px-6 py-3 rounded-xl transition-colors">
                  Learn About Us <FaArrowRight size={13} />
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {whyUs.map((item, i) => (
                <div key={i} className="p-5 bg-white bg-opacity-5 border border-white border-opacity-10 rounded-2xl hover:bg-opacity-10 transition-colors">
                  <div className="text-cyan-400 mb-3">{item.icon}</div>
                  <h4 className="font-semibold text-white text-sm mb-1.5">{item.title}</h4>
                  <p className="text-blue-300 text-xs leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-16 bg-white border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-center text-2xl font-bold text-[#0c1b4d] mb-2">Technologies We Work With</h2>
          <p className="text-center text-gray-400 text-sm mb-8">Deep expertise across the modern technology stack</p>
          <div className="flex flex-wrap justify-center gap-2.5">
            {techStack.map((tech, i) => (
              <span key={i} className="px-4 py-2 bg-slate-50 border border-gray-200 rounded-full text-sm font-medium text-gray-600 hover:border-blue-400 hover:text-blue-700 hover:bg-blue-50 transition-all cursor-default">
                {tech}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
  {/*     <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-cyan-600 text-sm font-semibold uppercase tracking-widest">Client Stories</span>
            <h2 className="text-3xl font-bold text-[#0c1b4d] mt-2">What Our Clients Say</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <svg key={j} className="w-4 h-4 text-amber-400 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-5 italic">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 ${t.color} rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
                    {t.initials}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{t.name}</p>
                    <p className="text-xs text-gray-400">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Industries */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <span className="text-cyan-600 text-sm font-semibold uppercase tracking-widest">Industries</span>
            <h2 className="text-2xl font-bold text-[#0c1b4d] mt-2">Sectors We Serve</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { icon: <FaDatabase size={22} />, label: 'Financial Services' },
              { icon: <FaCogs size={22} />, label: 'Healthcare & Life Sciences' },
              { icon: <FaServer size={22} />, label: 'Government & Public Sector' },
              { icon: <FaCloud size={22} />, label: 'Retail & E-Commerce' },
              { icon: <FaCode size={22} />, label: 'Technology & SaaS' },
              { icon: <FaChartBar size={22} />, label: 'Manufacturing & Logistics' },
            ].map((ind, i) => (
              <div key={i} className="flex flex-col items-center text-center p-4 rounded-xl border border-gray-100 hover:border-blue-200 hover:bg-blue-50 transition-all cursor-default">
                <div className="text-[#0c1b4d] mb-2">{ind.icon}</div>
                <span className="text-xs text-gray-600 font-medium leading-tight">{ind.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-[#0c1b4d] to-[#1d4ed8] text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Transform Your IT?</h2>
          <p className="text-blue-200 mb-8 text-lg">Let's discuss how MARVY can accelerate your digital transformation journey.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/contact" className="px-7 py-3 bg-white text-[#0c1b4d] font-bold rounded-xl hover:bg-blue-50 transition-colors shadow-lg">
              Contact Us Today
            </Link>
            <Link to="/careers" className="px-7 py-3 border border-white border-opacity-50 text-white font-semibold rounded-xl hover:bg-white hover:bg-opacity-10 transition-colors">
              Join Our Team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
